
import { initializeApp, deleteApp } from "firebase/app";
import { getStorage } from 'firebase/storage';
import { getAuth, createUserWithEmailAndPassword, 
    signInWithEmailAndPassword, signOut, onAuthStateChanged, 
    signInWithPopup, GoogleAuthProvider, sendPasswordResetEmail  
} from "firebase/auth";
import { toast } from 'sonner'
import { doc, getDoc , setDoc, getFirestore, collection, where, orderBy, addDoc, getDocs, query, updateDoc } from "firebase/firestore";

export { isAdminProfile, getAdminEmails, ADMIN_STATUS } from "./auth.utils";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error(
    'Missing Firebase env vars. Copy .env.example to .env and restart the dev server.'
  );
}

const firebaseApp = initializeApp(firebaseConfig);
export const imageDb = getStorage(firebaseApp);

export const auth = getAuth();
export const db = getFirestore();

const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: "select_account"
});
export const signInWithGooglePopup = () => signInWithPopup(auth, provider);

export const logGoogleUser = async () => {
    try {
      await signInWithGooglePopup();
    } catch (error) {
      switch (error.code) {
        case ('auth/popup-closed-by-user'):
            infoToast('Login window closed..!');
          break;
      
        default:
            infoToast('An error occured..!')
            console.log('An error occured: '+error.code);
          break;
      }
    }
    // const userDocRef = await createUserDocumentFromAuth();
    // console.log(user);
}

export const createAuth = async (email, password) => {
    if (!email || !password) return;
    return await createUserWithEmailAndPassword(auth, email, password);  
}

export const createUserDocumentFromAuth = async (userAuth, additionalinfo = {}) => {
    if (!userAuth) return null;
    const userDocRef = doc(db, 'users', userAuth.uid);
    const userSnapshot = await getDoc(userDocRef);

    if (!userSnapshot.exists()) {
        const { displayName, email } = userAuth;
        const createdAt = new Date();
        const uid = userAuth.uid;
        // Never accept client-supplied status — promote admins in Firestore Console only
        const { status: _ignoredStatus, del: _ignoredDel, uid: _ignoredUid, ...safeInfo } =
          additionalinfo || {};

      try {
        await setDoc(userDocRef, {
          ...safeInfo,
          uid,
          displayName: displayName || safeInfo.displayName || '',
          email,
          status: 'user',
          del: 'no',
          createdAt,
        });
      } catch (error) {
        console.log('An error occured', error.message);
      }
    }

    const refreshed = await getDoc(userDocRef);
    if (!refreshed.exists()) return null;
    return { ...refreshed.data(), id: refreshed.id };
}

export const signInAuthWithEmailAndPassword = async (email, password) => {
    if (!email || !password) return;
    return await signInWithEmailAndPassword(auth, email, password);  
}

export const signOutUser = async () => await signOut(auth);
export const resetPassword = (email) => sendPasswordResetEmail(auth, email);
export const onAuthStateChangeListener = (callback) => onAuthStateChanged(auth, callback);





// Inquiry

export const createInquiryDoc = async (docToAdd) => {
    const refValue = collection(db, 'inquiries');
    await addDoc(refValue, docToAdd);
}

export const updateInquiryDoc = async (inqDoc) => {
    const { id, ...data } = inqDoc;
    if (!id) throw new Error('Inquiry id is required to update');
    await updateDoc(doc(db, 'inquiries', id), {
        ...data,
        updated_at: new Date(),
    });
}

/** Queue a custom admin email; Cloud Function onOutboundMailCreated sends it. */
export const createOutboundMailDoc = async (docToAdd) => {
    const refValue = collection(db, 'outbound_mail');
    await addDoc(refValue, docToAdd);
}

export const getInquiryDocs = async () => {
    const docsReceiver = [];
    const querySnapshot = await getDocs(query(collection(db, 'inquiries'), orderBy("created_at", "desc")));

    const purMap = () => querySnapshot.forEach((doc) => {
        docsReceiver.push({...doc.data(), id: doc.id});
    });
    purMap();
    return docsReceiver;
}


// Clients

export const createGalleryDoc = async (docToAdd) => {
    const refValue = collection(db, 'gallery');
    await addDoc(refValue, docToAdd);
}

export const getGalleryDocs = async () => {
    const docsReceiver = [];
    try {
        const querySnapshot = await getDocs(
            query(collection(db, 'gallery'), orderBy("created_at", "desc"))
        );
        querySnapshot.forEach((docSnap) => {
            docsReceiver.push({ ...docSnap.data(), id: docSnap.id });
        });
    } catch (error) {
        console.error('Error loading gallery:', error.message);
        try {
            // Fallback if orderBy fails (missing field/index)
            const querySnapshot = await getDocs(collection(db, 'gallery'));
            querySnapshot.forEach((docSnap) => {
                docsReceiver.push({ ...docSnap.data(), id: docSnap.id });
            });
        } catch (fallbackError) {
            console.error('Gallery fallback failed:', fallbackError.message);
        }
    }
    return docsReceiver;
}

export const updateGalleryDoc = async (galleryDoc) => {
    const { id, ...data } = galleryDoc || {};
    if (!id) throw new Error('Gallery id is required to update');
    await updateDoc(doc(db, 'gallery', id), {
        ...data,
        updated_at: new Date(),
    });
}


// Clients

export const createOrderDoc = async (docToAdd) => {
    const refValue = collection(db, 'orders');
    await addDoc(refValue, docToAdd);
}

export const getOrderDocs = async () => {
    const docsReceiver = [];
    const querySnapshot = await getDocs(query(collection(db, 'orders'), orderBy("created_at", "desc")));

    const purMap = () => querySnapshot.forEach((doc) => {
        docsReceiver.push({...doc.data(), id: doc.id});
    });
    purMap();
    return docsReceiver;
}

export const updateOrderDoc = async (orderDoc) => {
    const { id, ...data } = orderDoc;
    if (!id) throw new Error('Order id is required to update');
    await updateDoc(doc(db, 'orders', id), {
        ...data,
        updated_at: new Date(),
    });
}


// Pages

export const createPagesDoc = async (docToAdd) => {
    const refValue = collection(db, 'pages');
    const allPages = await delAllPages();

    if (allPages) {
        const { id, ...prevData } = allPages;
        if (id) {
            await updateDoc(doc(db, 'pages', id), {
                ...prevData,
                del: 'yes',
                updated_at: new Date(),
            });
        }
    }

    const { id: _ignoredId, ...data } = docToAdd || {};
    await addDoc(refValue, {
        ...data,
        del: 'no',
        created_at: data.created_at || new Date(),
        updated_at: new Date(),
    });
}

export const getCurrentPage = async () => {
    const found = [];
    const searchRef = collection(db, "pages");

    const findOtp = await getDocs(query(searchRef, where("del", "==", "no")));
    const mapOtp = () => findOtp.forEach((docSnap) => {
        found.push({...docSnap.data(), id: docSnap.id});
    });
    mapOtp();
    return found[0];
}

export const getPagesDocs = async () => {
    const docsReceiver = [];
    const querySnapshot = await getDocs(query(collection(db, 'pages'), orderBy("created_at", "desc")));

    const purMap = () => querySnapshot.forEach((docSnap) => {
        docsReceiver.push({...docSnap.data(), id: docSnap.id});
    });
    purMap();
    return docsReceiver;
}

export const updatePagesDoc = async (page) => {
    const { id, ...data } = page || {};
    if (!id) throw new Error('Page id is required to update');
    await updateDoc(doc(db, 'pages', id), {
        ...data,
        updated_at: new Date(),
    });
}

export const delAllPages = async () => {
    const found = [];
    const searchRef = collection(db, "pages");

    const findOtp = await getDocs(query(searchRef, where("del", "==", "no")));
    const mapOtp = () => findOtp.forEach((docSnap) => {
        found.push({...docSnap.data(), id: docSnap.id});
    });
    mapOtp();
    return found[0];
}





// Generate OTP

export const OTPGen = async (limit) => {
    const otpRef = collection(db, 'otpgen');

    for (let i = 0; i < limit; i++) {
        var otpp = 'A'+Math.random()*107000;
        // console.log('OTPGen - '+otpp.substring(0, 5));
        console.log(i+' - '+otpp.substring(0, 5));
        try {
            await addDoc(otpRef, { otp: otpp.substring(0, 5), status: 'no'}).then(
                // window.location.reload()
            );
        } catch (error) {
            infoToast('Internet Disconnected');
            console.log('Error occoured: ', error.message);
        }
        
    }
    
}

export const searchOtp = async (otp) => {
    // const otpRef = await getDocs(query(collection(db, 'otpgen').where("otp", "==", otp)));

    const found = [];
    const otpRef = collection(db, "otpgen");

    const findOtp = await getDocs(query(otpRef, where("otp", "==", otp)));
    const mapOtp = () => findOtp.forEach((doc) => {
        found.push({...doc.data(), id: doc.id});
    });
    mapOtp();
    return found[0];
}

export const updateOTPDoc = async (otp) => {
    const upRefValue = doc(db, 'otpgen', otp.id);
    try {
        await updateDoc(upRefValue, otp);
    } catch (error) {
        console.log('Error occoured at OTP: ', error.message);
    }
}



// Users

export const searchUserDoc = async (email) => {
    const found = [];
    const otpRef = collection(db, "users");

    const findOtp = await getDocs(query(otpRef, where("email", "==", email)));
    const mapOtp = () => findOtp.forEach((doc) => {
        found.push({...doc.data()});
    });
    mapOtp();
    // console.log(found[0]);
    return found[0];
}

export const getUsersDocuments = async () => {
    const usersReceiver = [];
    try {
        const querySnapshot = await getDocs(query(collection(db, 'users'), orderBy("createdAt", "desc")));
        querySnapshot.forEach((docSnap) => {
            usersReceiver.push({ ...docSnap.data(), uid: docSnap.data().uid || docSnap.id, id: docSnap.id });
        });
    } catch (error) {
        console.error('Error loading users:', error.message);
        const querySnapshot = await getDocs(collection(db, 'users'));
        querySnapshot.forEach((docSnap) => {
            usersReceiver.push({ ...docSnap.data(), uid: docSnap.data().uid || docSnap.id, id: docSnap.id });
        });
    }
    return usersReceiver;
}

/**
 * Create a staff Auth user without signing the current admin out
 * (uses a temporary secondary Firebase app).
 */
export const createStaffUser = async ({
  email,
  password,
  displayName = '',
  asAdmin = true,
}) => {
  if (!email || !password) {
    throw new Error('Email and password are required');
  }

  const secondaryApp = initializeApp(firebaseConfig, `staff-${Date.now()}`);
  const secondaryAuth = getAuth(secondaryApp);

  try {
    const credential = await createUserWithEmailAndPassword(
      secondaryAuth,
      email.trim(),
      password
    );
    const { user } = credential;

    await createUserDocumentFromAuth(user, {
      displayName: (displayName || '').trim(),
    });

    await signOut(secondaryAuth);

    if (asAdmin) {
      await updateDoc(doc(db, 'users', user.uid), {
        status: 'admin',
        displayName: (displayName || '').trim(),
        updatedAt: new Date(),
      });
    }

    return { uid: user.uid, email: user.email };
  } finally {
    try {
      await deleteApp(secondaryApp);
    } catch (error) {
      console.warn('Could not dispose secondary auth app:', error.message);
    }
  }
}

export const updateUserDoc = async (userDoc) => {
    const { uid, id, ...data } = userDoc || {};
    const docId = uid || id;
    if (!docId) throw new Error('User uid is required to update');
    await updateDoc(doc(db, 'users', docId), {
        ...data,
        updatedAt: new Date(),
    });
}





// Toasts

export const successToast = (message) => {
  toast.success(message, {
    position: 'top-left',
    duration: '5000'
  })
}

export const errorToast = (message) => {
  toast.error(message, {
    position: 'top-left',
    duration: '5000'
  })
}

export const infoToast = (message) => {
  toast.info(message, {
    position: 'top-left',
    duration: '5000'
  })
}